import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import { useRoomContext } from '@livekit/components-react'
import { BsX, BsChevronRight, BsPeopleFill } from 'react-icons/bs'
import {
  VideoControlButton,
  MicOnIcon,
  MicOffIcon,
  CameraOnIcon,
  CameraOffIcon,
} from './ScreenShare/VideoControlButton'
import { DeviceSelectMenu } from './DeviceSelectMenu'
import { MediaControlGroup, VIDEO_OVERLAY_CONTROL_BG } from './styles/MediaControlGroup.styles'
import { useSpeakingParticipants } from '../hooks/useSpeakingParticipants'
import ParticipantsPanel from './ScreenShare/ParticipantsPanel'
import RaiseHandButton from './ScreenShare/RaiseHandButton'
import { useColyseusRoom } from '../hooks/useColyseusRoom'
import { useSharedRaisedHands } from './LiveKitVideoDisplay/context/RaisedHandsContext'
import inputController, { InputLockReason } from '../services/InputController'
import { emitDialogOpened, emitDialogClosed } from '../utils/pageVisibilityHandler'
import { useVirtualRoomFilteredParticipants } from '../hooks/useVirtualRoomFilteredParticipants'
import { useAppSelector } from '../hooks'
import { selectCurrentVirtualRoom, selectRoomOwner } from '../stores/VirtualRoomFilterStore'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState } from '../stores'
import { setWhiteboardConnectionStatus, removeWhiteboardUser } from '../stores/WhiteboardStore'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureInPictureAlt from '@mui/icons-material/PictureInPictureAlt'
import type { PictureInPictureSurface } from './LiveKitVideoDisplay/types/LiveKitVideoDisplay.types'
import { pipButtonState } from './GameMenu/callControlLabels'
import { useCameraControlState } from '../hooks/useCameraControlState'
import { SEVV_PRIMARY_CSS } from '../theme/sevvDialogChrome'

const Z_INDEX = { modal: 2000 }

export interface WhiteboardModalProps {
  whiteboardId: string
  whiteboardUrl: string
  participantId: string
  onClose: () => void
  toggleAudio: () => void
  toggleVideo: () => void
  audioEnabled: boolean
  videoEnabled: boolean
  /**
   * SC-209: this view covers the call bar, so it carries its own pop-out button. Absent means no button.
   */
  pictureInPicture?: PictureInPictureSurface
}

// --- Styled components ---

const ModalContainer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: Z_INDEX.modal,
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: 'var(--sevv-bg-default, #121420)',
})

const WhiteboardArea = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
})

// Foreground for anything drawn on `--sevv-surface-overlay` (the header and the loading/error overlay). That
// surface is a tint of the paper colour, which the theme pairs with `--sevv-text-primary` in both modes. Not
// `--sevv-white`: that is a surface colour too (white in light mode, dark paper in dark mode), so text in it
// vanished into the overlay in light mode and sank to low contrast in dark mode.
const ON_SURFACE_OVERLAY = 'var(--sevv-text-primary, #424242)'

const HeaderBar = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 16px',
  // Same fallback as StatusOverlay: the token's own light default, so with no theme applied the bar stays
  // light under ON_SURFACE_OVERLAY's dark fallback text instead of putting dark text on a dark bar.
  background: 'var(--sevv-surface-overlay, rgba(255, 255, 255, 0.72))',
  borderBottom: '1px solid var(--sevv-border-subtle, rgba(66, 66, 66, 0.1))',
  minHeight: '48px',
})

const Title = styled('div')({
  fontSize: '16px',
  fontWeight: 500,
  color: ON_SURFACE_OVERLAY,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const Controls = styled('div')({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
})

const IframeContainer = styled('div')({
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
})

const WhiteboardIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none',
  background: 'white',
})

const StatusOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  // The fallback is the token's own default (light paper tint), so with no theme applied the pair still
  // matches ON_SURFACE_OVERLAY's fallback rather than putting dark text on a dark navy.
  background: 'var(--sevv-surface-overlay, rgba(255, 255, 255, 0.72))',
  backdropFilter: 'blur(4px)',
  zIndex: 1,
  color: ON_SURFACE_OVERLAY,
})

const RetryButton = styled('button')({
  marginTop: '20px',
  padding: '8px 16px',
  // Inverse pairing: the fill is a text colour (gray-600 is text-secondary, gray-800 on hover is
  // text-primary), so the label is the paper colour the theme draws that text on, in either mode.
  background: 'var(--sevv-gray-600, #4a5568)',
  color: 'var(--sevv-bg-paper, #ffffff)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'background 0.2s',
  '&:hover': { background: 'var(--sevv-gray-800, #2d3748)' },
})

const BottomControls = styled('div')({
  position: 'absolute',
  bottom: '24px',
  right: '24px',
  display: 'flex',
  gap: '12px',
  zIndex: 10,
})

const WhiteboardModal: React.FC<WhiteboardModalProps> = ({
  whiteboardId,
  whiteboardUrl,
  participantId,
  onClose,
  toggleAudio,
  toggleVideo,
  audioEnabled,
  videoEnabled,
  pictureInPicture,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [showParticipants, setShowParticipants] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  // SC-158: the same reading as the call bar's camera button. A camera-on made while the
  // background pipeline rebuilds is dropped by the connector, so "on" is disabled for that window.
  const { disabled: cameraDisabled, label: cameraLabel } = useCameraControlState(videoEnabled)

  const connectionStatus = useSelector((state: RootState) =>
    whiteboardId ? state.whiteboard.connectionStatus[whiteboardId] : null
  )
  const activeUsers = useSelector((state: RootState) =>
    state.whiteboard.activeWhiteboardUsers[whiteboardId] || []
  )

  // Virtual room filtered participants & tracks
  const {
    participants: rawFilteredParticipants,
    cameraTracks: filteredCameraTracks,
  } = useVirtualRoomFilteredParticipants()

  const livekitRoom = useRoomContext()
  const colyseusRoom = useColyseusRoom()
  const { handRaisedSessionIds, localHandRaised, toggleHandRaise, canRaiseHand } = useSharedRaisedHands(colyseusRoom)
  const { speakingIds, speakingOrder } = useSpeakingParticipants()
  const currentRoom = useAppSelector(selectCurrentVirtualRoom)
  const ownerSessionId = useAppSelector((state) =>
    currentRoom ? selectRoomOwner(state, currentRoom.roomId) : undefined
  ) ?? undefined

  const participants = rawFilteredParticipants.length > 0 ? rawFilteredParticipants : []

  // Sort camera tracks: local first, then raised hands, then speaking, then rest (same order as screen-share)
  const sortedCameraTracks = useMemo(() => {
    const local = filteredCameraTracks.filter((t) => t.participant.isLocal)
    const remote = filteredCameraTracks.filter((t) => !t.participant.isLocal)
    const sortedRemote = [...remote].sort((a, b) => {
      const idA = a.participant.identity
      const idB = b.participant.identity
      const handA = handRaisedSessionIds.has(idA)
      const handB = handRaisedSessionIds.has(idB)
      if (handA !== handB) return handA ? -1 : 1
      const speakA = speakingOrder.indexOf(idA)
      const speakB = speakingOrder.indexOf(idB)
      if (speakA !== -1 && speakB !== -1) return speakA - speakB
      if (speakA !== -1) return -1
      if (speakB !== -1) return 1
      return 0
    })
    return [...local, ...sortedRemote]
  }, [filteredCameraTracks, speakingOrder, handRaisedSessionIds])

  const sortedCameraTracksWithSid = useMemo(() => {
    return sortedCameraTracks.map(t => ({
      ...t,
      trackSid: t.publication?.trackSid || (t.publication?.track as any)?.sid || '',
    })) as any
  }, [sortedCameraTracks])

  const groupedCameraTracksWithSid = useMemo(() => {
    const rows: any[][] = []
    for (let i = 0; i < sortedCameraTracksWithSid.length; i += 2) {
      rows.push(sortedCameraTracksWithSid.slice(i, i + 2))
    }
    return rows
  }, [sortedCameraTracksWithSid])

  const cameraTracksByParticipantId = useMemo(() => {
    const map = new Map<string, any>()
    sortedCameraTracksWithSid.forEach((t: any) => {
      map.set(t.participant.identity, t)
    })
    return map
  }, [sortedCameraTracksWithSid])

  // Lock input & emit dialog events on mount; disconnect from whiteboard on unmount
  const isInitiator = livekitRoom.localParticipant?.identity === participantId
  useEffect(() => {
    inputController.lockInput(InputLockReason.WHITEBOARD)
    emitDialogOpened('whiteboard')
    return () => {
      inputController.unlockInput(InputLockReason.WHITEBOARD)
      emitDialogClosed('whiteboard')
      // Only the initiator (who pressed R) disconnects — remote viewers just close the modal
      if (isInitiator) {
        const localIdentity = livekitRoom.localParticipant?.identity || ''
        // Immediately remove self from active users so badge disappears on local client
        dispatch(removeWhiteboardUser({ whiteboardId, sessionId: localIdentity }))
        // Signal the controller to clear debounce timestamp so the server's
        // ITEM_USER_REMOVED won't be incorrectly blocked by the debounce logic
        window.dispatchEvent(new CustomEvent('whiteboard-local-disconnect', {
          detail: { whiteboardId, participantId: localIdentity },
        }))
        const game = (window as any).gameScene as any
        game?.network?.disconnectFromWhiteboard?.(whiteboardId)
      }
    }
  }, [whiteboardId, isInitiator])

  // Escape key to close — only for non-initiators (remote viewers).
  // The initiator closes via the close button only (closing ends the whiteboard for everyone).
  useEffect(() => {
    if (isInitiator) return // initiator uses close button only
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown, true) // capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [onClose, isInitiator])

  // Auto-close when all users disconnect from the whiteboard (5s grace period).
  // Only applies to remote viewers — the initiator controls their own session.
  // Also requires a minimum uptime to ignore spurious ITEM_USER_REMOVED events
  // that the server may fire shortly after connection (e.g. VR area cleanup race).
  const hadUsersRef = useRef(false)
  const mountTimeRef = useRef(Date.now())
  useEffect(() => {
    if (activeUsers.length > 0) {
      hadUsersRef.current = true
    }
    // Don't auto-close for the initiator — they close manually via Escape / close button.
    // Don't auto-close within the first 10s — ignore spurious server-side removal events.
    if (!isInitiator && hadUsersRef.current && activeUsers.length === 0) {
      const elapsed = Date.now() - mountTimeRef.current
      if (elapsed < 10000) {
        return
      }
      const timer = setTimeout(() => onClose(), 5000)
      return () => clearTimeout(timer)
    }
  }, [activeUsers.length, onClose, isInitiator])

  // Connection timeout
  useEffect(() => {
    if (connectionStatus === 'connecting') {
      const timeoutId = setTimeout(() => {
        dispatch(setWhiteboardConnectionStatus({ whiteboardId, status: 'timeout' }))
      }, 10000)
      return () => clearTimeout(timeoutId)
    }
  }, [connectionStatus, whiteboardId, dispatch])

  useEffect(() => {
    if (connectionStatus === 'timeout' || connectionStatus === 'failed') {
      setIsLoading(false)
      setHasError(true)
    } else if (connectionStatus === 'connected') {
      setIsLoading(false)
      setHasError(false)
    }
  }, [connectionStatus])

  // Store status is what the timeout effect watches — without flipping it to 'connected'
  // here, a board that loaded fine would still show the timeout overlay after 10s.
  const handleIframeLoad = useCallback(() => {
    // Retry swaps src through about:blank first; that load says nothing about WBO.
    if (iframeRef.current?.src === 'about:blank') return
    setIsLoading(false)
    setHasError(false)
    dispatch(setWhiteboardConnectionStatus({ whiteboardId, status: 'connected' }))
  }, [dispatch, whiteboardId])

  const handleIframeError = useCallback(() => {
    setIsLoading(false)
    setHasError(true)
  }, [])

  const handleRetry = useCallback(() => {
    if (!iframeRef.current || !whiteboardUrl) return
    setIsLoading(true)
    setHasError(false)
    dispatch(setWhiteboardConnectionStatus({ whiteboardId, status: 'connecting' }))
    const iframe = iframeRef.current
    iframe.src = 'about:blank'
    setTimeout(() => { iframe.src = whiteboardUrl }, 100)
  }, [whiteboardUrl, whiteboardId, dispatch])

  // SC-209: only the document window can show a board. Safari's single-video window would fall through
  // to a face (`pickSafariTrack` skips whiteboards), so offering it here would promise the wrong thing.
  // In document mode `canOpen` is always true, so there is no disabled state to draw.
  const pipButton = pipButtonState(pictureInPicture, { documentOnly: true })
  const showPipButton = pipButton.show
  const pipLabel = pipButton.label

  return (
    <ModalContainer ref={containerRef}>
      <WhiteboardArea>
        <HeaderBar>
          <Title>Whiteboard</Title>
          <Controls>
            <IconButton
              aria-label="reload whiteboard"
              onClick={handleRetry}
              size="small"
              style={{ color: ON_SURFACE_OVERLAY }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            {/* The glyph is white in both modes, and the header is a light paper tint in light mode, so
                the button's own faint default fill left it barely visible. This is the bottom controls'
                fill: in dark mode the disc blends into the header but the white X still stands out.
                The inline fill drops the hover darkening; scale and shadow remain, as on raise hand. */}
            <VideoControlButton
              onClick={onClose}
              aria-label="Close whiteboard"
              style={{ padding: '6px', backgroundColor: VIDEO_OVERLAY_CONTROL_BG }}
            >
              {React.createElement(BsX as any, { size: 20 })}
            </VideoControlButton>
          </Controls>
        </HeaderBar>

        <IframeContainer>
          <WhiteboardIframe
            ref={iframeRef}
            title={`whiteboard-${whiteboardId}`}
            src={whiteboardUrl}
            sandbox="allow-downloads allow-scripts allow-same-origin allow-forms"
            referrerPolicy="no-referrer"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />

          {isLoading && (
            <StatusOverlay>
              <CircularProgress size={48} thickness={4} style={{ color: 'var(--sevv-primary, #6366f1)' }} />
              <p style={{ marginTop: '16px', fontSize: '16px' }}>Loading whiteboard...</p>
            </StatusOverlay>
          )}

          {hasError && (
            <StatusOverlay>
              <div style={{ color: 'var(--sevv-error, #f56565)', fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
              <p style={{ marginTop: '16px', fontSize: '16px', textAlign: 'center', maxWidth: '80%' }}>
                {connectionStatus === 'timeout'
                  ? 'Connection timed out. The whiteboard server may be slow to respond.'
                  : 'Unable to load the whiteboard. The server might be unavailable.'}
              </p>
              <RetryButton onClick={handleRetry}>
                <RefreshIcon fontSize="small" />
                Try Again
              </RetryButton>
            </StatusOverlay>
          )}
        </IframeContainer>

        <BottomControls>
          <MediaControlGroup>
            <VideoControlButton
              active={audioEnabled}
              muted={!audioEnabled}
              onClick={toggleAudio}
              aria-label={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
              title={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
              style={{ width: '40px', height: '40px' }}
            >
              {audioEnabled ? <MicOnIcon /> : <MicOffIcon />}
            </VideoControlButton>
            <DeviceSelectMenu kind="audioinput" />
          </MediaControlGroup>
          <MediaControlGroup>
            <VideoControlButton
              active={videoEnabled}
              muted={!videoEnabled}
              onClick={toggleVideo}
              disabled={cameraDisabled}
              aria-label={cameraLabel}
              title={cameraLabel}
              style={{
                width: '40px',
                height: '40px',
              }}
            >
              {videoEnabled ? <CameraOnIcon /> : <CameraOffIcon />}
            </VideoControlButton>
            <DeviceSelectMenu kind="videoinput" />
          </MediaControlGroup>
          {canRaiseHand && (
            <RaiseHandButton raised={localHandRaised} onToggle={toggleHandRaise} disabled={!colyseusRoom} />
          )}
          {/* SC-209: this view covers the call bar, so the pop-out sits with the call controls, in the
              share view's order. Popping out must not touch this modal: closing it as the initiator ends
              the board for everyone, and the window loads its own iframe. */}
          {showPipButton && (
            <VideoControlButton
              type="button"
              onClick={pipButton.toggle}
              aria-label={pipLabel}
              aria-pressed={pictureInPicture.isOpen}
              title={pipLabel}
              data-testid="whiteboard-pip-button"
              data-pip-state={pictureInPicture.isOpen ? 'open' : 'closed'}
              style={{
                width: '40px',
                height: '40px',
                // Raise hand's look: the button's own resting fill is too faint for a white glyph on the
                // white canvas, and the filled primary disc is what marks "open".
                backgroundColor: pictureInPicture.isOpen
                  ? SEVV_PRIMARY_CSS
                  : VIDEO_OVERLAY_CONTROL_BG,
                border: pictureInPicture.isOpen ? `2px solid ${SEVV_PRIMARY_CSS}` : 'none',
              }}
            >
              <PictureInPictureAlt fontSize="small" />
            </VideoControlButton>
          )}
        </BottomControls>
      </WhiteboardArea>

      {showParticipants && (
        <ParticipantsPanel
          sortedCameraTracks={sortedCameraTracksWithSid}
          groupedCameraTracks={groupedCameraTracksWithSid}
          allParticipants={participants}
          cameraTracksByParticipantId={cameraTracksByParticipantId}
          speakingIds={speakingIds}
          handRaisedSessionIds={handRaisedSessionIds}
          cameraViewMode="single"
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          toggleAudio={toggleAudio}
          toggleVideo={toggleVideo}
          room={colyseusRoom}
          ownerSessionId={ownerSessionId}
          canLowerHands={isInitiator}
        />
      )}

      {/* Toggle participants panel button (edge) */}
      <div
        style={{
          position: 'absolute',
          right: showParticipants ? '280px' : '0px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 20,
        }}
      >
        <VideoControlButton
          onClick={() => setShowParticipants((p) => !p)}
          aria-label={showParticipants ? 'Hide participants' : 'Show participants'}
          style={{
            // It sits over the white canvas (panel hidden) or the panel's edge, so it shares the
            // bottom controls' resting fill rather than keeping a copy of the value.
            backgroundColor: VIDEO_OVERLAY_CONTROL_BG,
            backdropFilter: 'blur(4px)',
            width: '32px',
            height: '32px',
          }}
        >
          {showParticipants
            ? React.createElement(BsChevronRight as any, { size: 16 })
            : React.createElement(BsPeopleFill as any, { size: 16 })}
        </VideoControlButton>
      </div>
    </ModalContainer>
  )
}

export default WhiteboardModal
